import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const spinnerVariants = cva("shrink-0 animate-spin motion-reduce:animate-none", {
  variants: {
    size: {
      dot: "size-1.5",
      xs: "size-3",
      sm: "size-4",
      default: "size-6",
      lg: "size-8",
    },
    // `current` takes the ink of what it sits in (a busy Button).
    tone: {
      highlight: "text-highlight",
      current: "",
    },
  },
  defaultVariants: { size: "default", tone: "highlight" },
});

export interface SpinnerProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">, VariantProps<typeof spinnerVariants> {
  /** What is loading, read by screen readers. */
  label?: string;
  /** Inside a live region or a busy control that already announces the state: the ring alone, hidden from screen readers. */
  decorative?: boolean;
}

/** Indeterminate loading: a ring that turns, still under reduced motion. Put the words beside it; a page fallback is LoadingPage. */
function Spinner({ size, tone, label = "Loading", decorative = false, className, ...props }: SpinnerProps) {
  const ring = (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={spinnerVariants({ size, tone })}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity={0.15} strokeWidth={2} />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
  if (decorative)
    return (
      <span aria-hidden="true" className={cn("inline-flex", className)} {...props}>
        {ring}
      </span>
    );
  return (
    <span role="status" className={cn("inline-flex", className)} {...props}>
      {ring}
      <span className="sr-only">{label}</span>
    </span>
  );
}

export { Spinner, spinnerVariants };
